/**
 * Dashboard layout — which sections a portal user shows, and in what order.
 *
 * Pure, and kept out of the hook, because the hook imports the Supabase client
 * and therefore Vite's env, which the node test runner cannot load. The rules
 * here are the ones that must survive a release: stored preferences outlive the
 * code that wrote them.
 *
 * Storage shape is an array of `{ id, visible }` entries, because
 * `user_preferences.pinned_widgets` carries a check constraint requiring a JSON
 * array. A bare array of visible ids would have been smaller but ambiguous: a
 * section missing from it could mean "the user hid this" or "this did not exist
 * when the row was written", and those must behave differently — a hidden
 * section stays hidden, a new one appears.
 */

/** Every dashboard section a user may hide or reorder. */
export const DASHBOARD_WIDGETS = [
  'metrics',
  'sites',
  'alerts',
  'systems',
  // Operational health and SLA compliance share one two-column grid, so they
  // move and hide together rather than leaving a lone card in a half row.
  'health',
  'trips',
  'activity',
] as const;

export type DashboardWidget = (typeof DASHBOARD_WIDGETS)[number];

export interface DashboardLayout {
  /** Widgets in display order, including hidden ones. */
  order: DashboardWidget[];
  hidden: DashboardWidget[];
}

/** One stored entry. */
export interface StoredWidget {
  id: DashboardWidget;
  visible: boolean;
}

export const DEFAULT_LAYOUT: DashboardLayout = {
  order: [...DASHBOARD_WIDGETS],
  hidden: [],
};

function isWidget(value: unknown): value is DashboardWidget {
  return typeof value === 'string' && (DASHBOARD_WIDGETS as readonly string[]).includes(value);
}

/**
 * Turn whatever is stored into a complete, current layout.
 *
 * Unknown ids are dropped, duplicates collapse, and any section the stored row
 * never mentioned is appended and shown — a section added in a later release
 * must not stay invisible forever because an old row predates it.
 */
export function normalizeLayout(stored: unknown): DashboardLayout {
  if (!Array.isArray(stored)) return DEFAULT_LAYOUT;

  const seen = new Set<DashboardWidget>();
  const order: DashboardWidget[] = [];
  const hidden: DashboardWidget[] = [];

  for (const raw of stored) {
    if (!raw || typeof raw !== 'object' || Array.isArray(raw)) continue;
    const entry = raw as { id?: unknown; visible?: unknown };
    if (!isWidget(entry.id) || seen.has(entry.id)) continue;
    seen.add(entry.id);
    order.push(entry.id);
    if (entry.visible === false) hidden.push(entry.id);
  }

  // Nothing usable in the row: treat it as no preference rather than as a
  // deliberate empty dashboard.
  if (order.length === 0) return DEFAULT_LAYOUT;

  for (const widget of DASHBOARD_WIDGETS) {
    if (!seen.has(widget)) order.push(widget);
  }

  return { order, hidden };
}

/** The value to persist. */
export function toStoredLayout(layout: DashboardLayout): StoredWidget[] {
  return layout.order.map((id) => ({ id, visible: !layout.hidden.includes(id) }));
}

/** Move a widget one place up or down, returning a new order. */
export function moveWidget(
  order: DashboardWidget[],
  widget: DashboardWidget,
  direction: -1 | 1,
): DashboardWidget[] {
  const index = order.indexOf(widget);
  if (index === -1) return order;
  const target = index + direction;
  if (target < 0 || target >= order.length) return order;
  const next = [...order];
  [next[index], next[target]] = [next[target], next[index]];
  return next;
}
